/* SupportHub — topbar "Support & feedback" entry point (marker:
 * support-hub-icon).
 *
 * A dedicated support surface beyond the notification bell: a lifebuoy icon
 * in the shell toolbar that opens the Support & feedback modal (report an
 * issue → AI-answered ticket; wish list → refined feature request to Bot
 * Pros; my submissions + answers). The heavy modal lives in its own lazy
 * chunk (src/shared/SupportHubModal.tsx, marker: support-hub-modal) so the
 * shell pays ~nothing until the icon is clicked.
 *
 * Badge: a small green count appears when one of MY submissions gained a
 * new answer since I last had the hub open — driven by the pure helpers in
 * src/lib/supportHub/unseen.ts (localStorage last-seen snapshot; unit-
 * tested, docs/REGRESSIONS.md). Polled gently (mount +8s, then every 5 min,
 * visible-only via startManagedInterval); while the modal is open its own
 * 20s poller owns freshness and marks answers seen.
 */
import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { fetchMySupportSubmissions } from '../api'
import { SUPPORT_SEEN_STORAGE_KEY, countUnseenAnswers, parseSeenKeys } from '../lib/supportHub/unseen'
import { startManagedInterval } from '../polling'
import type { SupportHubTab } from './SupportHubModal'

const SupportHubModal = lazy(() => import('./SupportHubModal'))

const BADGE_POLL_MS = 5 * 60_000
const FIRST_POLL_DELAY_MS = 8_000 // stay out of the boot request burst

export default function SupportHub() {
  const [open, setOpen] = useState(false)
  const [unseen, setUnseen] = useState(0)
  const [initialTab, setInitialTab] = useState<SupportHubTab>('issue')
  const btnRef = useRef<HTMLButtonElement>(null)
  const openRef = useRef(false)
  useEffect(() => { openRef.current = open }, [open])

  useEffect(() => {
    let stop: (() => void) | null = null
    const timer = window.setTimeout(() => {
      stop = startManagedInterval({
        name: 'support-hub-badge',
        intervalMs: BADGE_POLL_MS,
        runImmediately: true,
        run: async () => {
          if (openRef.current) return // the open modal owns freshness + seen-marking
          try {
            const subs = await fetchMySupportSubmissions()
            let seen: string[] = []
            try { seen = parseSeenKeys(localStorage.getItem(SUPPORT_SEEN_STORAGE_KEY)) } catch { seen = [] }
            setUnseen(countUnseenAnswers(subs, seen))
          } catch { /* badge is best-effort — keep the last known state */ }
        },
      })
    }, FIRST_POLL_DELAY_MS)
    return () => {
      window.clearTimeout(timer)
      if (stop) stop()
    }
  }, [])

  const openHub = () => {
    // Answers waiting → land the user straight on them.
    setInitialTab(unseen > 0 ? 'mine' : 'issue')
    setOpen(true)
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="hs-topbar-icon-btn support-hub-icon"
        title="Support & feedback"
        aria-label={unseen > 0 ? `Support & feedback — ${unseen} new answer${unseen === 1 ? '' : 's'}` : 'Support & feedback'}
        aria-haspopup="dialog"
        onClick={openHub}
        style={{ position: 'relative', color: '#fff', marginRight: '4px' }}
      >
        {/* Lifebuoy — every svg in a flex row keeps flex-shrink:0 (frontend rule). */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="4" />
          <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
          <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
          <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
          <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
        </svg>
        {unseen > 0 && !open && (
          <span className="support-hub-badge" aria-hidden="true">{unseen > 9 ? '9+' : unseen}</span>
        )}
      </button>
      {open && (
        <Suspense fallback={null}>
          <SupportHubModal
            initialTab={initialTab}
            onClose={() => {
              setOpen(false)
              btnRef.current?.focus()
            }}
            onAnswersSeen={() => setUnseen(0)}
          />
        </Suspense>
      )}
    </>
  )
}
